import { useApp } from '../context/AppContext';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { Badge } from '../components/ui/Badge';
import { PasswordChange } from '../components/PasswordChange';
import { TeacherSettings } from './TeacherSettings';
import { TeacherClassPicker } from './TeacherClassPicker';
import { MoreMenu } from '../layout/MoreMenu';
import { BackButton } from '../components/ui/BackButton';
import { secondaryTabsByRole } from '../layout/roleTabs';
import DatePicker from '../components/DatePicker';
import { TeacherStrengthReminder } from '../components/TeacherStrengthReminder';
import { TeacherNutritionSection } from '../components/TeacherNutritionSection';
import { TeacherHistoryPanel } from '../components/TeacherHistoryPanel';
export function TeacherDashboard({ activeTab, onNavigate }) {
  const app = useApp();
  const {
    pending,
    labels,
    token,
    userInfo,
    strengthForm,
    setStrengthForm,
    selectedDate,
    setSelectedDate,
    updateStrength,
    isLoading,
    online,
    subscriptionWritesBlocked,
    teacherSubmissionStatus,
    isLoadingTeacherSubmission,
    fetchTeacherSubmissionStatus,
    nutritionForm,
    setNutritionForm,
    nutritionRecords,
    fetchTeacherNutrition,
    updateNutritionItem,
    updateNutritionMilk,
    clearNutritionItems,
  } = app;

  if (activeTab === 'more') {
    return <MoreMenu role="teacher" labels={labels} onSelect={onNavigate} />;
  }

  const showMobileBack = secondaryTabsByRole.teacher.includes(activeTab);

  if (activeTab === 'account') {
    return (
      <div>
        {showMobileBack && (
          <BackButton onClick={() => onNavigate('more')} label={labels.back} />
        )}
        <PasswordChange token={token} labels={labels} />
      </div>
    );
  }

  if (activeTab === 'switchClass') {
    return <TeacherClassPicker onNavigate={onNavigate} />;
  }

  if (activeTab === 'settings') {
    return (
      <div>
        {showMobileBack && (
          <BackButton onClick={() => onNavigate('more')} label={labels.back} />
        )}
        <TeacherSettings />
      </div>
    );
  }

  if (activeTab === 'history') {
    return (
      <div>
        {showMobileBack && (
          <BackButton onClick={() => onNavigate('more')} label={labels.back} />
        )}
        <TeacherHistoryPanel />
      </div>
    );
  }

  const boys = parseInt(strengthForm.boys, 10) || 0;
  const girls = parseInt(strengthForm.girls, 10) || 0;
  const maxBoys = parseInt(strengthForm.max_boys, 10) || 0;
  const maxGirls = parseInt(strengthForm.max_girls, 10) || 0;
  const maxStudents = parseInt(strengthForm.max_students, 10) || 0;
  const total = boys + girls;
  const legacyMax = maxBoys === 0 && maxGirls === 0 ? maxStudents : 0;

  return (
    <div className="space-y-4 pb-4">
      <TeacherStrengthReminder
        labels={labels}
        submissionStatus={teacherSubmissionStatus}
        isLoading={isLoadingTeacherSubmission}
        onRefresh={() => fetchTeacherSubmissionStatus(selectedDate?.slice(0, 10))}
        selectedDate={selectedDate}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={labels.division} value={strengthForm.division_name || '—'} className="col-span-2 sm:col-span-1" />
        <StatCard label={labels.maxBoys} value={maxBoys > 0 ? maxBoys : '—'} />
        <StatCard label={labels.maxGirls} value={maxGirls > 0 ? maxGirls : '—'} />
        <StatCard
          label={labels.totalStudents}
          value={total}
          sub={
            maxBoys > 0 || maxGirls > 0
              ? `${boys}/${maxBoys || '—'} · ${girls}/${maxGirls || '—'}`
              : legacyMax
                ? `${total} / ${legacyMax}`
                : undefined
          }
        />
      </div>

      <Card>
        <CardHeader title={labels.myClass} subtitle={userInfo?.name} />
        <CardBody className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text">{labels.date}</label>
            <DatePicker selectedDate={selectedDate} onDateChange={setSelectedDate} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={labels.boys}
              id="teacher-boys"
              name="boys"
              type="number"
              min={0}
              inputMode="numeric"
              autoComplete="off"
              value={strengthForm.boys}
              onChange={(e) => setStrengthForm({ ...strengthForm, boys: e.target.value })}
            />
            <Input
              label={labels.girls}
              id="teacher-girls"
              name="girls"
              type="number"
              min={0}
              inputMode="numeric"
              autoComplete="off"
              value={strengthForm.girls}
              onChange={(e) => setStrengthForm({ ...strengthForm, girls: e.target.value })}
            />
          </div>

          {!online && pending > 0 && (
            <Badge variant="warning">{labels.pendingSync}</Badge>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={updateStrength}
            isLoading={isLoading.updateStrength}
            disabled={subscriptionWritesBlocked}
          >
            {labels.updateStrength}
          </Button>
        </CardBody>
      </Card>

      <TeacherNutritionSection
        labels={labels}
        nutritionForm={nutritionForm}
        setNutritionForm={setNutritionForm}
        nutritionRecords={nutritionRecords}
        selectedDate={selectedDate}
        classId={strengthForm.class_id}
        fetchTeacherNutrition={fetchTeacherNutrition}
        updateNutritionItem={updateNutritionItem}
        updateNutritionMilk={updateNutritionMilk}
        clearNutritionItems={clearNutritionItems}
        isLoading={isLoading}
        subscriptionWritesBlocked={subscriptionWritesBlocked}
        maxBoys={maxBoys}
        maxGirls={maxGirls}
      />
    </div>
  );
}
